import { AxiosInstance, AxiosResponse } from "axios";
import { makeAutoObservable, runInAction } from "mobx";
import { download, DownloadResult, DownloadService } from "../api/DownloadService";
import { LoggedOnUserInfoDTO } from "../types/UserInfo";
import {
  failure,
  isOk,
  isPending,
  ok,
  pending,
  RenderedDecisionRow,
  Result,
} from "../common/common";
import { AccessType } from "../types/enums/AccessType";
import { UploadedAttachment } from "../fileUpload/fileUploadDomain";
import { Decision } from "../dbot/project/projectDomain";
import { MeetingEmailActions } from "./noticeForBoardmemberComponents";
import { History } from "history";
import { BoardMeetingVote, Vote } from "../dbot/boardMeetingMinutes/boardMeetingVoting";
import { Language } from "../i18n/languages";

import { TMessage } from "../i18n/tt";
import { PreviewState } from "../meetings/meetingsDomain";

import { StringFieldModel } from "../common/commonForms";
import { removeTitleAndTD } from "../utils/other";
import { CommentDTO } from "../comments/commentsDomain";
import { downloadFile } from "../api";
import { DownloadAsPdfButtonProps } from "../common/commonComponents";
import { DocumentType } from "../common/documents";
import { EventDispatcher, showError, showSuccess } from "../events/eventsDomain";

export type MatterForReview = {
  decision: Decision;
  textForReview: RenderedDecisionRow[];
  votes: BoardMeetingVote[];
  attachments: UploadedAttachment[];
  comments: CommentDTO[];
  isMultiLanguage: boolean;
};

export type GetMeetingEmailDocumentResponse = {
  meetingEmailId: string;
  noticeDocumentId?: string;
  noticeDocumentContent?: string;
  minutesDocumentContent?: string;
  draftMinutesDocumentContent?: string;
  mattersForReview: MatterForReview[];
  uploadedFiles: UploadedAttachment[];
  isDeleted?: boolean;
  companyName?: string;
  orgNo: string;
  recipientName?: string;
  recipientEmail?: string;
  recipientInitials?: string;
  meetingStartDate?: string;
  meetingEndDate?: string;
  meetingLocation?: string;
  userInfo?: LoggedOnUserInfoDTO;
  accessType: AccessType;
  ical: {
    [key in Language]: string;
  };
  googleCalendar: {
    [key in Language]: string;
  };
  projectId: string;
  companyroleId?: number;
  isVoting: boolean;
  comments: CommentDTO[];
  isCirculation: boolean;
  deadlineForFeedback?: string;
  isMultiLanguage: boolean;
  authStatus?: MeetingAuthStatus;
  companyLogo?: string;
};

export type MeetingAuthStatus =
  | "PASSWORD_NEEDED"
  | "WRONG_PASSWORD"
  | "PASSWORD_BLOCKED"
  | "LOGIN_NEEDED";

export type HeaderData = {
  companyName?: string;
  orgNo?: string;
  recipientName?: string;
  recipientEmail?: string;
  recipientInitials?: string;
  minutesDocumentContent?: string;
  accessType: AccessType;
  ical?: {
    [key in Language]: string;
  };
  googleCalendar?: {
    [key in Language]: string;
  };
  meetingStartDate?: string;
  meetingEndDate?: string;
  meetingLocation?: string;
  isCirculation: boolean;
  deadlineForFeedback?: string;
  companyroleId?: number;
};

export class MeetingEmailInitializerController {
  dispatch: EventDispatcher;
  http: AxiosInstance;
  result: Result<MeetingEmailController, TMessage> = pending();
  readonly onSetUserInfo: (userInfo: LoggedOnUserInfoDTO) => void;
  meetingPassword: StringFieldModel = new StringFieldModel();

  constructor(
    dispatch: EventDispatcher,
    http: AxiosInstance,
    onSetUserInfo: (userInfo: LoggedOnUserInfoDTO) => void,
  ) {
    this.dispatch = dispatch;
    this.http = http;
    this.onSetUserInfo = onSetUserInfo;
    makeAutoObservable(this, { http: false });
  }

  reloadReview(documentId?: string) {
    if (documentId) {
      const reviewTabNeedsRefresh = isOk(this.result);
      if (reviewTabNeedsRefresh) {
        this.fetchPreviewForAdmin(documentId);
      }
    }
  }

  fetchDocument(emailId: string): Promise<GetMeetingEmailDocumentResponse> {
    const password = this.meetingPassword.inputValue;
    return this.http
      .get<GetMeetingEmailDocumentResponse>(`/boardmember-view/notice-board-meeting/${emailId}`, {
        auth: password ? { username: "user", password: password } : undefined,
      })
      .then((r) => r.data);
  }

  *fetchMatter(emailId: string, matterId: string) {
    try {
      this.setResult(pending());
      const response: GetMeetingEmailDocumentResponse = yield this.fetchDocument(emailId);
      const controller = new MeetingEmailController(
        response,
        this.dispatch,
        this.http,
        new DownloadService(this.http),
      );
      controller.fetchMatter(emailId, matterId);
      this.setResult(ok(controller));
      if (response.userInfo) {
        this.onSetUserInfo(response.userInfo);
      }
    } catch (err) {
      this.setResult(failure({ key: "noticeForBoardMember.loadNoticeError" }));
    }
  }

  *fetchDocumentFromMeetingEmail(emailId: string) {
    try {
      this.setResult(pending());
      const response: GetMeetingEmailDocumentResponse = yield this.fetchDocument(emailId);
      this.setResult(
        ok(
          new MeetingEmailController(
            response,
            this.dispatch,
            this.http,
            new DownloadService(this.http),
          ),
        ),
      );
      if (response.userInfo) {
        this.onSetUserInfo(response.userInfo);
      }
      this.meetingPassword.clear();
    } catch (err) {
      this.setResult(failure({ key: "noticeForBoardMember.loadNoticeError" }));
    }
  }

  *fetchPreviewForAdmin(documentId: string) {
    try {
      this.setResult(pending());
      const response: GetMeetingEmailDocumentResponse = yield this.http
        .get(`/boardmember-view/admin-review/${documentId}`)
        .then((r) => r.data);
      this.setResult(
        ok(
          new MeetingEmailController(
            response,
            this.dispatch,
            this.http,
            new DownloadService(this.http),
          ),
        ),
      );
      if (response.userInfo) {
        this.onSetUserInfo(response.userInfo);
      }
    } catch (err) {
      this.setResult(failure({ key: "noticeForBoardMember.loadNoticeError" }));
    }
  }

  setResult(result: Result<MeetingEmailController, TMessage>) {
    runInAction(() => (this.result = result));
  }
}

export class MeetingEmailController {
  dispatch: EventDispatcher;
  http: AxiosInstance;
  response: GetMeetingEmailDocumentResponse;
  downloadService: DownloadService;
  noticeDownloadResult?: Result;
  showDownloadNonPdfWarning = false;
  votingResult?: Result<BoardMeetingVote[], TMessage>;
  commentResult?: Result<CommentDTO[], TMessage>;
  matterResult?: Result<MatterForReview, TMessage>;

  updateCommentId?: string;
  deleteCommentId?: string;
  commentText?: string;
  showEditor?: boolean;

  previewState?: PreviewState;
  downloadNoticeAndAttachmentsUrl: string;
  downloadNoticeUrl: string;

  constructor(
    response: GetMeetingEmailDocumentResponse,
    dispatch: EventDispatcher,
    http: AxiosInstance,
    downloadService: DownloadService,
    isAdminPreview = false,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.http = http;
    this.response = response;
    this.downloadService = downloadService;

    this.downloadNoticeAndAttachmentsUrl = isAdminPreview
      ? `/boardmember-view/preview-meeting/download-notice-and-attachments/${this.response.noticeDocumentId}`
      : `/boardmember-view/notice-board-meeting/${this.response.meetingEmailId}/download-notice-and-attachments/${this.response.noticeDocumentId}`;
    this.downloadNoticeUrl = isAdminPreview
      ? `/document/view/pdf/${this.response.noticeDocumentId}`
      : `/boardmember-view/notice-board-meeting/${this.response.meetingEmailId}/download-as-pdf/${this.response.noticeDocumentId}`;
  }

  get isDeleted(): boolean {
    return this.response.isDeleted ?? false;
  }

  get matter(): MatterForReview | undefined {
    if (isOk(this.matterResult)) {
      return this.matterResult.data;
    }
    return undefined;
  }

  setMatter(matter?: MatterForReview) {
    if (matter) {
      this.matterResult = ok(matter);
    } else {
      this.matterResult = undefined;
    }
  }

  *fetchMatter(meetingEmailId: string, decisionId: string) {
    this.matterResult = pending();
    try {
      const response: AxiosResponse<MatterForReview> = yield this.http.get<MatterForReview>(
        `/boardmember-view/review/${meetingEmailId}/matter/${decisionId}`,
      );
      this.setMatter(response.data);
    } catch (e) {
      this.matterResult = failure({ key: "general-error" });
    }
  }

  openPreviewAttachmentDialog(fileId: string) {
    this.previewState = {
      type: "preview",
      filename: this.response.uploadedFiles.find((d) => d.id === fileId)?.filename ?? "",
      file: {
        url: `/api/boardmember-view/notice-board-meeting/${this.response.meetingEmailId}/attachment/${fileId}`,
      },
    };
  }

  closePreviewAttachmentDialog() {
    this.previewState = undefined;
  }

  get downloadNotice(): DownloadAsPdfButtonProps {
    return {
      isDownloading: isPending(this.noticeDownloadResult),
      onConfirm: this.showDownloadNonPdfWarning ? this.downloadNoticeAndAttachments : undefined,
      onCancel: this.cancelDownloadNoticeAndAttachments,
      onDownload: this.downloadDocumentAsPDF,
      onDownloadWithAttachments:
        this.response.uploadedFiles.length > 0 ? this.downloadNoticeAndAttachments : undefined,
      documentType: DocumentType.notice_board_meeting,
    };
  }

  cancelDownloadNoticeAndAttachments() {
    this.showDownloadNonPdfWarning = false;
  }

  *downloadNoticeAndAttachments() {
    if (hasNonPdfAttachment(this.response.uploadedFiles) && !this.showDownloadNonPdfWarning) {
      this.showDownloadNonPdfWarning = true;
      return;
    }
    try {
      this.noticeDownloadResult = pending();
      const [file, filename]: DownloadResult = yield download(
        this.downloadNoticeAndAttachmentsUrl,
        this.http,
      );
      if (filename?.endsWith(".pdf") && file instanceof Blob) {
        this.previewState = {
          type: "preview",
          filename,
          file: { data: file },
        };
      } else {
        downloadFile(file, filename);
      }
      this.noticeDownloadResult = ok();
    } catch (e) {
      this.noticeDownloadResult = failure();
      this.dispatch([showError({ key: "archive.download-error" })]);
    } finally {
      this.showDownloadNonPdfWarning = false;
    }
  }

  *downloadDocumentAsPDF() {
    const [file, filename]: DownloadResult = yield download(this.downloadNoticeUrl, this.http);
    if (filename?.endsWith(".pdf") && file instanceof Blob) {
      this.previewState = {
        type: "preview",
        filename,
        file: { data: file },
      };
    } else {
      downloadFile(file, filename);
    }
  }

  toReviewPage(history: History) {
    this.matterResult = undefined;
    history.push(`/board-minutes-review/${this.response.meetingEmailId}`);
  }

  *voteOnMatter(matter: MatterForReview, vote: Vote) {
    this.votingResult = pending();
    try {
      const response: AxiosResponse<BoardMeetingVote[]> = yield this.http.post(
        `/boardmember-view/review/${this.response.meetingEmailId}/vote`,
        {
          decisionId: matter.decision.decisionId,
          vote,
        },
      );
      this.votingResult = ok(response.data);
      if (isOk(this.matterResult)) {
        this.matterResult.data.votes = response.data;
      }
      this.dispatch([showSuccess({ key: "meetings.vote-success" })]);
    } catch (e) {
      this.votingResult = failure({ key: "general-error" });
    }
  }

  get hasVoted() {
    if (isOk(this.matterResult)) {
      const companyroleId = this.response.companyroleId;
      const vote = this.matterResult.data.votes.find(
        (vote) => vote.companyroleId === companyroleId,
      )?.vote;
      return vote !== undefined;
    } else {
      return false;
    }
  }

  *resetVote() {
    if (!isOk(this.matterResult)) {
      return;
    }
    this.votingResult = pending();
    try {
      const response: AxiosResponse<BoardMeetingVote[]> = yield this.http.delete(
        `/boardmember-view/review/${this.response.meetingEmailId}/reset-vote/${this.matterResult?.data?.decision.decisionId}`,
      );
      this.votingResult = ok(response.data);
      if (isOk(this.matterResult)) {
        this.matterResult.data.votes = response.data;
      }
      this.dispatch([showSuccess({ key: "meetings.vote-success" })]);
    } catch (e) {
      this.votingResult = failure({ key: "general-error" });
    }
  }

  saveComment(decisionId?: string) {
    if (this.updateCommentId === undefined) {
      this.commentOnMatterAsParticipant(decisionId);
    } else {
      this.updateCommentOnMatterAsParticipant();
    }
  }

  *commentOnMatterAsParticipant(decisionId?: string) {
    const comments: CommentDTO[] = yield this.postComment(
      `/boardmember-view/review/${this.response.meetingEmailId}/comment`,
      decisionId,
    );
    if (decisionId === undefined) {
      this.response.comments = comments;
    }
  }

  onSaveAsAdmin(decisionId?: string) {
    if (this.updateCommentId === undefined) {
      this.commentAsAdmin(decisionId);
    } else {
      this.updateCommentAsAdmin();
    }
  }

  *commentAsAdmin(decisionId?: string) {
    const comments: CommentDTO[] = yield this.postComment(
      `/boardmember-view/admin-review/${this.response.noticeDocumentId}/comment`,
      decisionId,
    );
    if (decisionId === undefined) {
      this.response.comments = comments;
    }
  }

  *postComment(url: string, decisionId?: string) {
    this.commentResult = pending();
    try {
      const response: AxiosResponse<CommentDTO[]> = yield this.http.post(url, {
        decisionId,
        commentText: this.commentText ?? "",
      });
      this.commentResult = ok(response.data);
      if (isOk(this.matterResult) && decisionId !== undefined) {
        this.matterResult.data.comments = response.data;
      }
      this.resetCommentEditing();
      return response.data;
    } catch (e) {
      this.commentResult = failure({ key: "general-error" });
    }
  }

  updateCommentOnMatterAsParticipant() {
    this.updateCommentOnMatter(
      this.commentText ?? "",
      `/boardmember-view/review/${this.response.meetingEmailId}/update-comment/${this.updateCommentId}`,
    );
  }

  updateCommentAsAdmin() {
    this.updateCommentOnMatter(
      this.commentText ?? "",
      `/boardmember-view/admin-review/${this.response.noticeDocumentId}/update-comment/${this.updateCommentId}`,
    );
  }

  *updateCommentOnMatter(commentText: string, url: string) {
    this.commentResult = pending();
    try {
      const response: AxiosResponse<CommentDTO[]> = yield this.http.put(url, {
        commentText,
      });
      this.commentResult = ok(response.data);
      let comment = this.response.comments.find((c) => c.id === this.updateCommentId);
      if (isOk(this.matterResult)) {
        this.matterResult.data.comments = response.data;
        if (!comment) {
          comment = this.matterResult.data.comments.find((c) => c.id === this.updateCommentId);
        }
      }
      if (comment) {
        comment.commentText = commentText;
      }
      this.resetCommentEditing();
    } catch (e) {
      this.commentResult = failure({ key: "general-error" });
    }
  }

  resetCommentEditing() {
    this.commentText = undefined;
    this.showEditor = false;
    this.updateCommentId = undefined;
    this.deleteCommentId = undefined;
  }

  deleteCommentOnMatterAsParticipant(commentId: string) {
    this.deleteComment(
      `/boardmember-view/review/${this.response.meetingEmailId}/delete-comment/${commentId}`,
    );
  }

  deleteCommentAsAdmin(commentId: string) {
    this.deleteComment(
      `/boardmember-view/admin-review/${this.response.noticeDocumentId}/delete-comment/${commentId}`,
    );
  }

  *deleteComment(url: string) {
    this.commentResult = pending();
    try {
      const response: AxiosResponse<CommentDTO[]> = yield this.http.delete(url);
      this.commentResult = ok(response.data);
      if (isOk(this.matterResult)) {
        this.matterResult.data.comments = this.matterResult.data.comments.filter(
          (c) => c.id !== this.deleteCommentId,
        );
      }
      this.response.comments = this.response.comments.filter((c) => c.id !== this.deleteCommentId);
      this.resetCommentEditing();
    } catch (e) {
      this.commentResult = failure({ key: "general-error" });
    }
  }

  get results(): (
    | Result<void | MatterForReview | BoardMeetingVote[] | CommentDTO[], TMessage>
    | undefined
  )[] {
    return [this.votingResult, this.commentResult, this.matterResult];
  }

  setUpdateComment(comment?: CommentDTO) {
    this.updateCommentId = comment?.id;
    this.commentText = comment?.commentText;
    this.showEditor = true;
  }

  setDeleteCommentId(commentId?: string) {
    this.deleteCommentId = commentId;
  }

  onToggleEditor() {
    this.showEditor = !this.showEditor;
  }

  updateEditorText(text: string) {
    this.commentText = text;
  }
}

export function normalizeMatterText(matter: MatterForReview) {
  if (matter.textForReview.length > 2) {
    return removeHtml(
      matter.textForReview
        .filter((_, i) => i === 2)
        .flatMap((row) => row.cells)
        .join(" "),
    );
  } else {
    return removeHtml(matter.textForReview.flatMap((row) => row.cells).join(" "));
  }
}

export function removeHtml(html: string) {
  const tempDivElement = document.createElement("div");
  tempDivElement.innerHTML = removeTitleAndTD(html);
  const clean = tempDivElement.textContent || tempDivElement.innerText || "";
  return clean.replace(/\s+/g, " ");
}

export function createEditComment(ctrl: MeetingEmailController) {
  return {
    updateCommentId: ctrl.updateCommentId,
    deleteCommentId: ctrl.deleteCommentId,
    commentText: ctrl.commentText,
    showEditor: ctrl.showEditor,
  };
}

export function createActionsProps(
  reviewMinutesData: GetMeetingEmailDocumentResponse,
  ctrl: MeetingEmailController,
  history: History,
): MeetingEmailActions {
  const hasBoardMemberAccess = reviewMinutesData.companyroleId !== undefined;
  return {
    onVoteForMatter:
      reviewMinutesData.isVoting && hasBoardMemberAccess ? ctrl.voteOnMatter : undefined,
    onDownloadAttachment: ctrl.openPreviewAttachmentDialog,
    onBack: () => ctrl.toReviewPage(history),
    commentActions: {
      onSave: ctrl.saveComment,
      onEditComment: ctrl.setUpdateComment,
      onDeleteComment: ctrl.setDeleteCommentId,
      onConfirmDelete: ctrl.deleteCommentOnMatterAsParticipant,
      onToggleEditor: ctrl.onToggleEditor,
      onCancel: ctrl.resetCommentEditing,
      onChange: ctrl.updateEditorText,
    },
    results: ctrl.results,
  };
}

export const hasNonPdfAttachment = (attachments?: UploadedAttachment[]) =>
  attachments && attachments.some((a) => !a.filename.toLowerCase().endsWith(".pdf"));
